import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import addIcon from "@/assets/drbfm/add.svg";
import docsIcon from "@/assets/drbfm/docs.svg";
import { Button } from "@/components/ui/button";
import DocumentPickerDialog from "../document-selector/DocumentPickerDialog";
import { documentSourceErrorMessage } from "../workflow/mainline/mainlineErrorMessage";
import { useRegisteredDocumentSource } from "./useRegisteredDocumentSource";
import type { DocumentSourceSlotProps } from "./types";

export default function RegisteredDocumentSource(
  props: DocumentSourceSlotProps,
) {
  const { t } = useTranslation();
  const picker = useRegisteredDocumentSource(props);
  const label = t(`drbfm.firstRunInput.fields.${props.role}`);
  return (
    <>
      {props.value ? (
        <div className="rounded-lg border border-[#dfe4ec] p-2 text-base leading-6 dark:border-border">
          <div className="flex min-h-12 items-center gap-3 rounded-lg bg-[#f1f5f9] px-4 py-3 text-sm dark:bg-muted">
            <img
              src={docsIcon}
              alt=""
              className="shrink-0 brightness-0 dark:invert"
            />
            <span className="min-w-0 flex-1 break-words font-medium">
              {props.value.fileName || props.value.title}
            </span>
            <Button
              type="button"
              variant="link"
              disabled={props.disabled}
              className="h-auto shrink-0 p-0 text-xs text-[#0084ff]"
              onClick={() => picker.changeOpen(true)}
              aria-label={`${label}: ${t("drbfm.firstRun.chooseDocument")}`}
            >
              {t("drbfm.firstRun.changeDocument")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={props.disabled}
              className="size-6 shrink-0"
              aria-label={`${label}: ${t("drbfm.mainline.remove")}`}
              onClick={() => props.onChange(null)}
            >
              <X aria-hidden="true" />
            </Button>
          </div>
          {props.onEditCopy && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-2"
              disabled={!picker.canCopyPreview}
              onClick={picker.copyPreview}
            >
              {t("drbfm.firstRunInput.editCopy")}
            </Button>
          )}
          <div className="mt-2 max-h-[726px] overflow-auto">
            {picker.previewError ? (
              <p role="alert">
                {documentSourceErrorMessage(t, picker.previewError, "preview")}
              </p>
            ) : picker.preview ? (
              <pre
                aria-label={t("drbfm.firstRun.preview")}
                className="whitespace-pre-wrap break-words [font-family:inherit] leading-6"
              >
                {picker.preview.content}
              </pre>
            ) : (
              <p role="status">{t("common.loading")}</p>
            )}
          </div>
        </div>
      ) : (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={props.disabled}
          onClick={() => picker.changeOpen(true)}
          className="absolute bottom-1 left-1 size-7"
          aria-label={`${label}: ${t("drbfm.firstRun.chooseDocument")}`}
        >
          <img src={addIcon} alt="" className="dark:invert" />
        </Button>
      )}
      <DocumentPickerDialog
        open={picker.open}
        onOpenChange={picker.changeOpen}
        documents={picker.documents}
        selectedDocumentId={props.value?.documentId}
        searchQuery={picker.query}
        onSearchQueryChange={picker.changeQuery}
        loading={picker.busy && !picker.documents.length}
        loadingMore={picker.busy && !!picker.documents.length}
        totalCount={picker.total}
        canLoadMore={picker.documents.length < picker.total}
        contentLoading={picker.selecting}
        fetchError={
          picker.error ? documentSourceErrorMessage(t, picker.error) : null
        }
        onLoadMore={() => void picker.search(true)}
        onSelect={(doc) => void picker.select(doc)}
        onRetry={() => void picker.search(false, picker.query)}
        onCancel={() => picker.changeOpen(false)}
      />
    </>
  );
}
